import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  apiErrorText,
  apiLoadErrorText,
  econChronumApi,
  useListEconChronumCostCentersQuery,
  useListEconChronumSitesQuery,
  useSetEconChronumSiteMutation,
  type ChronumSite,
  type ChronumSiteBody,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import { ScrollRegion, VisuallyHidden } from '@entities/fleet';
import {
  costCenterSuggestions,
  formatDateTime,
  formatInt,
  originLabel,
  plural,
  siteCounts,
  siteNeedsAttention,
  sortSites,
  typo,
} from '@entities/chronum';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { isStaleError } from '../apiErrors';
import type { SaveError } from '../MappingParts';
import type { SiteChange } from '../SiteEditor';
import { EmptyBox, ListSkeleton, SectionNote } from '../ui';
import {
  Bar,
  Count,
  historyTag,
  PanelRoot,
  Table,
  Toggle,
} from './styles';
import { SiteRow } from './SiteRow';

export interface Props {
  canWrite: boolean;
  attentionOnly: boolean;
  onAttentionOnly: (on: boolean) => void;
  /** Площадка из аномалии: открыть её привязку. */
  focusSite: string | null;
  onFocusConsumed: () => void;
  /** Есть доступ к Руководителю — стройки ведут в профиль объекта. */
  progressAccess: boolean;
}

/**
 * «Площадки»: куда идут часы каждой площадки Хронума. Стройки привязывает
 * Руководитель (здесь — только показ, с тем, как привязана: вручную или по
 * названию), центр затрат и комментарий — Экономист, с журналом правок.
 * Порядок строк держится, пока человек работает: исправленная площадка не
 * уезжает из-под руки.
 */
export function SitesPanel({
  canWrite,
  attentionOnly,
  onAttentionOnly,
  focusSite,
  onFocusConsumed,
  progressAccess,
}: Props) {
  const dispatch = useAppDispatch();
  const query = useListEconChronumSitesQuery();
  const centers = useListEconChronumCostCentersQuery();
  const [save] = useSetEconChronumSiteMutation();
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, SaveError>>({});
  /** «Перечитать»: какая площадка и с какого момента ждёт свежий список. */
  const [rereading, setRereading] = useState<{ id: string; since: number } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [order, setOrder] = useState<readonly string[] | null>(null);
  const [keep, setKeep] = useState<ReadonlySet<string>>(() => new Set());
  /** Кнопка строки, которая открывает привязку. */
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  /** «Снять центр затрат» у двойной привязки. */
  const removers = useRef(new Map<string, HTMLButtonElement>());
  const rowHeads = useRef(new Map<string, HTMLTableCellElement>());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  /**
   * Куда вернуть фокус после закрытия привязки: «Отмена» — на кнопку строки,
   * запись — на саму строку (кнопка после записи может исчезнуть).
   */
  const focusNext = useRef<{ id: string; to: 'trigger' | 'remover' | 'row' } | null>(null);

  const items = query.data?.items;
  useEffect(() => {
    if (items && order === null) setOrder(sortSites(items).map((s) => s.id));
  }, [items, order]);

  const ordered = useMemo(() => {
    if (!items) return [];
    const rank = new Map((order ?? []).map((id, i) => [id, i]));
    const known = items.filter((s) => rank.has(s.id));
    const fresh = sortSites(items.filter((s) => !rank.has(s.id)));
    known.sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
    return [...known, ...fresh];
  }, [items, order]);

  const visible = attentionOnly
    ? ordered.filter((s) => siteNeedsAttention(s) || keep.has(s.id))
    : ordered;
  const counts = siteCounts(items ?? []);
  const attention = counts.unbound + counts.conflict;
  const used = centers.data?.items;
  const suggestions = useMemo(() => costCenterSuggestions(used ?? []), [used]);
  const knownCodes = useMemo(() => (used ?? []).map((c) => c.code), [used]);
  const snapshot = query.data?.snapshot ?? null;

  // «Перечитать» закончилось, когда пришёл список, запрошенный после нажатия.
  const fulfilledAt = query.fulfilledTimeStamp ?? 0;
  useEffect(() => {
    if (!rereading || fulfilledAt <= rereading.since) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[rereading.id];
      return next;
    });
    setRereading(null);
  }, [rereading, fulfilledAt]);

  // Переход из аномалии: непривязанной площадке — сразу поле центра затрат,
  // двойной привязке — кнопка «Снять центр затрат» (остальное — у Руководителя).
  useEffect(() => {
    if (!focusSite || !items) return;
    const site = items.find((s) => s.id === focusSite);
    onFocusConsumed();
    if (!site) return;
    if (canWrite && site.unbound) setEditing(site.id);
    else if (canWrite && site.conflict) focusNext.current = { id: site.id, to: 'remover' };
    else focusNext.current = { id: site.id, to: 'trigger' };
  }, [focusSite, items, canWrite, onFocusConsumed]);

  // Открыли привязку — фокус в поле кода (или комментария, если кода нет).
  useEffect(() => {
    if (!editing) return;
    const target = inputRef.current ?? commentRef.current;
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: 'center' });
  }, [editing]);

  // Закрыли привязку или пришли из аномалии — фокус на кнопку или строку.
  useLayoutEffect(() => {
    const next = focusNext.current;
    if (!next || editing) return;
    focusNext.current = null;
    const target =
      (next.to === 'remover' ? removers.current.get(next.id) : undefined) ??
      (next.to !== 'row' ? triggers.current.get(next.id) : undefined) ??
      rowHeads.current.get(next.id);
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: 'nearest' });
  });

  function toggleAttention() {
    setKeep(new Set());
    setOrder(items ? sortSites(items).map((s) => s.id) : null);
    onAttentionOnly(!attentionOnly);
  }

  function closeEditor(siteId: string) {
    setEditing(null);
    focusNext.current = { id: siteId, to: 'trigger' };
  }

  function clearError(siteId: string) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[siteId];
      return next;
    });
  }

  function reread(siteId: string) {
    setRereading({ id: siteId, since: Date.now() });
    dispatch(
      econChronumApi.util.invalidateTags([
        { type: 'EconChronum', id: 'SITES' },
        { type: 'EconChronum', id: 'COST_CENTERS' },
        historyTag(siteId),
      ]),
    );
  }

  async function saveSite(site: ChronumSite, change: SiteChange) {
    setBusy(site.id);
    clearError(site.id);
    const body: ChronumSiteBody = { name: site.name, expected_updated_at: site.updated_at };
    if (change.code !== undefined) body.cost_center_code = change.code;
    if (change.comment !== undefined) body.comment = change.comment;
    try {
      const res = await save({ siteId: site.id, body }).unwrap();
      if (attentionOnly) setKeep((prev) => new Set(prev).add(site.id));
      const code = res.site?.cost_center_code ?? null;
      const parts: string[] = [];
      if (change.code !== undefined) {
        parts.push(code ? `центр затрат «${code}» сохранён` : 'центр затрат снят');
      }
      if (change.comment !== undefined) {
        parts.push(change.comment ? 'комментарий сохранён' : 'комментарий удалён');
      }
      setAnnouncement(`Площадка «${site.name}»: ${parts.join(', ')}.`);
      setEditing(null);
      focusNext.current = { id: site.id, to: 'row' };
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [site.id]: {
          message: apiErrorText(err, 'Не удалось сохранить привязку площадки.'),
          stale: isStaleError(err),
        },
      }));
    } finally {
      setBusy(null);
    }
  }

  if (query.isError && !query.data) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(
            query.error,
            'Не удалось загрузить площадки Хронума.',
            'Нет доступа к площадкам Хронума.',
          )}
        </span>
        <TextButton type="button" onClick={() => void query.refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (query.isLoading || !query.data) {
    return <ListSkeleton rows={6} label="Загрузка площадок" />;
  }
  if (ordered.length === 0) {
    return (
      <EmptyBox>
        <h3>Площадок пока нет</h3>
        <p>
          {typo(
            snapshot
              ? 'В последнем списке Хронума нет площадок у компаний ключа Экономиста.'
              : 'Список площадок приходит из Хронума с первой загрузкой — по кнопке «Обновить» или по расписанию.',
          )}
        </p>
      </EmptyBox>
    );
  }

  return (
    <PanelRoot aria-busy={query.isFetching}>
      <Bar>
        <Count aria-live="polite">
          <strong>
            {formatInt(counts.total)} {plural(counts.total, ['площадка', 'площадки', 'площадок'])}
          </strong>
          {counts.unbound > 0 && ` · не привязаны — ${formatInt(counts.unbound)}`}
          {counts.conflict > 0 && ` · с двойной привязкой — ${formatInt(counts.conflict)}`}
          {attention === 0 && ' · все привязаны'}
        </Count>
        {(attention > 0 || attentionOnly) && (
          <Toggle type="button" aria-pressed={attentionOnly} onClick={toggleAttention}>
            Только требующие внимания · {formatInt(attention)}
          </Toggle>
        )}
      </Bar>
      <SectionNote>
        {typo(
          'Стройку площадке выбирает Руководитель в профиле объекта — здесь она только видна: выбранная вручную или подобранная по названию объекта, как на дашборде Руководителя. Центр затрат указывается для площадок без стройки: база, офис, ОГМ.',
        )}
        {snapshot &&
          ` Список Хронума от ${formatDateTime(snapshot.fetched_at)} (${originLabel(snapshot)}).`}
      </SectionNote>

      <ScrollRegion label="Площадки Хронума — таблица">
        <Table>
          <thead>
            <tr>
              <th scope="col">Площадка Хронума</th>
              <th scope="col">Стройка (у Руководителя)</th>
              <th scope="col">Центр затрат</th>
              <th scope="col">Состояние</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((site) => {
              const isEditing = editing === site.id;
              const error = errors[site.id] ?? null;
              return (
                <SiteRow
                  key={site.id}
                  site={site}
                  canWrite={canWrite}
                  progressAccess={progressAccess}
                  isEditing={isEditing}
                  busy={busy === site.id}
                  error={error}
                  rereading={rereading?.id === site.id}
                  suggestions={suggestions}
                  knownCodes={knownCodes}
                  removers={removers}
                  triggers={triggers}
                  rowHeads={rowHeads}
                  inputRef={inputRef}
                  commentRef={commentRef}
                  onSaveSite={saveSite}
                  onRemoveCostCenter={(s) => void saveSite(s, { code: null })}
                  onStartEdit={(id) => setEditing(id)}
                  onCancelEdit={(id) => {
                    clearError(id);
                    closeEditor(id);
                  }}
                  onReread={reread}
                />
              );
            })}
          </tbody>
        </Table>
      </ScrollRegion>
      <VisuallyHidden role="status" aria-live="polite">
        {announcement}
      </VisuallyHidden>
    </PanelRoot>
  );
}
